/**
 * Share API Service
 * Handles all API calls for project sharing functionality
 */

// Use production server for API calls (works both locally and in production)
const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://floorplanpro.net/api'
  : '/api';

/**
 * Create a new share link for a project
 * @param {Object} projectData - The floor plan project data
 * @param {Object} options - Share options
 * @param {string} options.title - Project title
 * @param {number} options.expiresInDays - Days until expiration (0 = never)
 * @param {boolean} options.allowComments - Allow client comments
 * @returns {Promise<{success: boolean, shareId: string, shareUrl: string}>}
 */
export async function createShare(projectData, options = {}) {
  const { title = 'Untitled Floor Plan', expiresInDays = 0, allowComments = false } = options;

  const response = await fetch(`${API_BASE}/share`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      projectData,
      title,
      expiresInDays: expiresInDays || null,
      allowComments,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create share' }));
    throw new Error(error.error || 'Failed to create share');
  }

  return response.json();
}

/**
 * Get a shared project by ID
 * @param {string} shareId - The share ID
 * @returns {Promise<{success: boolean, title: string, projectData: Object, createdAt: string, viewCount: number, allowComments: boolean}>}
 */
export async function getShare(shareId) {
  const response = await fetch(`${API_BASE}/share/${shareId}`);

  if (response.status === 404) {
    throw new Error('Share not found');
  }

  if (response.status === 410) {
    throw new Error('Share has expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to load share' }));
    throw new Error(error.error || 'Failed to load share');
  }

  return response.json();
}

/**
 * Add a comment to a shared project
 * @param {string} shareId - The share ID
 * @param {Object} comment - Comment data
 * @param {string} comment.authorName - Commenter's name
 * @param {string} comment.commentText - Comment text
 * @param {number} comment.positionX - X position on canvas
 * @param {number} comment.positionY - Y position on canvas
 * @returns {Promise<{success: boolean, commentId: number}>}
 */
export async function addComment(shareId, comment) {
  const { authorName, commentText, positionX, positionY } = comment;

  const response = await fetch(`${API_BASE}/share/${shareId}/comments`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      authorName,
      commentText,
      positionX,
      positionY,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to add comment' }));
    throw new Error(error.error || 'Failed to add comment');
  }

  return response.json();
}

/**
 * Get all comments for a shared project
 * @param {string} shareId - The share ID
 * @returns {Promise<{success: boolean, comments: Array}>}
 */
export async function getComments(shareId) {
  const response = await fetch(`${API_BASE}/share/${shareId}/comments`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to load comments' }));
    throw new Error(error.error || 'Failed to load comments');
  }

  return response.json();
}

/**
 * Delete a share and all its comments
 * @param {string} shareId - The share ID
 * @returns {Promise<{success: boolean}>}
 */
export async function deleteShare(shareId) {
  const response = await fetch(`${API_BASE}/share/${shareId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete share' }));
    throw new Error(error.error || 'Failed to delete share');
  }

  return response.json();
}
